/*
 * File: ActivityCard.tsx
 * Project: a-a
 * File Created: Thursday, 14th November 2024 9:10:41 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 25th August 2025 9:35:06 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import queryString from 'query-string';
import { Box, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import zeplinColor from '../../../../../../theme/Color';
import CustomCard from '../../../../CustomCard/CustomCard';
import Divider from '@mui/material/Divider';
import { LightTooltip } from '../../../../CustomTooltip/CustomTooltip';
import styles from './ActivityCard.module.css';
import { ReactComponent as SlackIcon } from '../../../../../../assets/slack-icon.svg';
import { ReactComponent as SlackUser } from '../../../../../../assets/slack-user.svg';
import CustomButton from '../../../../CustomButton/CustomButton';
import { Link, useLocation, useNavigate } from 'react-router-dom';
const ActivityCard = ({
  val,
  index,
  newData,
  loading,
  isExpanded = true,
  isFirstCard = false, // Optional prop to indicate if this is the first card in the stack
  total = 0, // Optional prop to indicate the total number of cards
  handleCardClick = () => {}, // Function to handle card click
  slackConnectBtn = false, // Optional prop to show Slack connect button
  setShowSlackConnect = () => {}, // Function to set show Slack connect state
}: {
  val: any;
  index: any;
  newData: any;
  loading: boolean;
  isExpanded?: boolean;
  isFirstCard?: boolean; // Optional prop to indicate if this is the first card in the stack
  total?: number; // Optional prop to indicate the total number of cards
  handleCardClick?: () => void; // Function to handle card click
  slackConnectBtn?: boolean; // Optional prop to show Slack connect button
  setShowSlackConnect?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  // console.log(val);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const now = new Date(); // Current time
  const eventDate = new Date(val?.date); // Replace val?.date with the actual date string
  let timeDifference = 0;
  let timeText = '';
  const handleRedirect = () => {
    sessionStorage.setItem('sf_redirect', 'Overview');
    sessionStorage.setItem('sf_path', location?.pathname);
    sessionStorage.setItem('slackConnecting', 'true');
  };
  if (isNaN(eventDate?.getTime())) {
    // Handle invalid date
    console.log('Invalid date:', val?.date);
  } else {
    timeDifference = now?.getTime() - eventDate?.getTime(); // getTime() gives milliseconds
    console.log(timeDifference);
  }

  // Convert milliseconds to minutes and hours
  const minutesAgo = Math.floor(timeDifference / 1000 / 60); // Minutes
  const hoursAgo = Math.floor(timeDifference / 1000 / 60 / 60); // Hours
  // Responsive breakpoints for text length
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1200px
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl')); // 1200px - 1536px
  // Get responsive text length threshold
  const getTextLengthThreshold = () => {
    if (isXs) return 20; // Very small screens
    if (isSm) return 25; // Small screens
    if (isMd) return 28; // Medium screens
    if (isLg) return 35; // Large screens
    return 58; // Extra large screens
  };
  const getCardHeight = () => {
    const textLengthThreshold = getTextLengthThreshold();
    // Only apply height logic for Slack cards
    if (!val?.isSlack && !slackConnectBtn) {
      return 'auto'; // Salesforce cards use auto height
    }

    if (slackConnectBtn) return '128px';

    if (isFirstCard) {
      // First Slack card: base height + channel section + notification count
      let height = val?.text && val.text.length > textLengthThreshold ? 99 : 79; // Base height
      if (val?.channel) height += 35; // Channel section
      // if (val?.text && val.text.length > 66) height += 20; // Long text
      if (total > 1 && !isExpanded) height += 25; // Space for "more notifications" text
      return `${height}px`;
    } else {
      // Subsequent Slack cards
      if (!isExpanded) {
        // In collapsed state, use same height as first card for proper stacking
        let height = val?.text && val.text.length > textLengthThreshold ? 99 : 79;
        if (val?.channel) height += 35;
        // if (val?.text && val.text.length > 66) height += 20;
        // if (total > 1) height += 25;
        return `${height}px`;
      } else {
        // In expanded state: just sender + text + time
        let height = val?.text && val.text.length > textLengthThreshold ? 99 : 79; // Base height for subsequent cards
        // if (val?.text && val.text.length > 66) height += 15; // Long text
        return `${height}px`;
      }
    }
  };

  // If it's less than an hour, show minutes; otherwise, show hours
  if (hoursAgo > 23) {
    timeText = eventDate?.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
    });
  } else if (hoursAgo > 0) {
    timeText = `${hoursAgo}h ago`;
  } else if (minutesAgo > 0) {
    timeText = `${minutesAgo}m ago`;
  } else {
    timeText = 'Just now';
  }

  const getDateDetails = (date: string, label: string) => (
    <Box
      className={styles.createdByContainer}
      sx={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'flex-end' }}
    >
      <LightTooltip
        title={
          <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
            {new Date(date)?.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </Typography>
        }
        placement='bottom-end'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Typography
          variant='responsiveBody3'
          sx={{
            display: 'block',
            width: {
              xs: type === 'new account' ? '120px' : '80px',
              xl: type === 'new account' ? '150px' : '100px',
            },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {date
            ? new Date(date)?.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'NA'}
        </Typography>
      </LightTooltip>
      <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
        {label}
      </Typography>
    </Box>
  );

  const getStageDetails = (stage: string, label: string) => (
    <Box
      className={styles.createdByContainer}
      sx={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'flex-end' }}
    >
      <LightTooltip
        title={
          <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
            {stage}
          </Typography>
        }
        placement='bottom-end'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Typography
          variant='responsiveBody3'
          sx={{
            display: 'block',
            width: {
              xs: type === 'new account' ? '80px' : '50px',
              xl: type === 'new account' ? '120px' : '100px',
            },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {stage || 'NA'}
        </Typography>
      </LightTooltip>
      <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
        {label}
      </Typography>
    </Box>
  );
  const getStatusDetails = (status: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'flex-end' }}>
      <Typography variant='responsiveBody3'>{status}</Typography>
      <Typography variant='responsiveLabel1'>Status</Typography>
    </Box>
  );

  const getCreatedBy = (createdBy: string, label: string) => (
    <Box
      className={styles.createdByContainer}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        justifyContent: 'flex-end',
      }}
    >
      <LightTooltip
        title={
          <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
            {createdBy}
          </Typography>
        }
        placement='bottom-end'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Typography
          variant='responsiveBody3'
          sx={{
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: { xs: type === 'new account' ? '120px' : '80px' },
            '@media (min-width:1700px)': {
              width: type === 'new account' ? '180px' : '130px', // More space on very large screens
            },
          }}
        >
          {createdBy || 'NA'}
        </Typography>
      </LightTooltip>
      <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
        {label}
      </Typography>
    </Box>
  );

  const getSubject = (type: string, val: any) => {
    const createTooltipContent = (content: string) => (
      <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
        {content ? content : '-'}
      </Typography>
    );

    const createTextWithTooltip = (content: React.ReactNode, fullText: string) => (
      <LightTooltip
        title={createTooltipContent(fullText)}
        placement='bottom-start'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Typography
          variant='responsiveBody3'
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            lineHeight: 1.4,
            // height: '2.8em', // Enforce exact height for 2 lines (2 * 1.4em)
            maxWidth: {
              xs: val?.isEdited ? '130px' : '170px',
              sm: val?.isEdited ? '210px' : '250px',
              md: val?.isEdited ? '260px' : '300px',
              lg: val?.isEdited ? '360px' : '400px',
            },
            // wordBreak: 'break-all', // This helps prevent partial words
            position: 'relative', // Required for the pseudo-element
            // '&::after': {
            //   content: '""',
            //   position: 'absolute',
            //   bottom: 0,
            //   right: 0,
            //   width: '10px', // Width of fade effect
            //   height: '1.4em', // Height of one line
            //   background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1) 80%)',
            //   pointerEvents: 'none', // Make it non-interactive
            // },
          }}
        >
          {content ? content : '-'}{' '}
          {val?.isEdited && (
            <Typography variant='label1' color={zeplinColor.Neutral8}>
              (edited)
            </Typography>
          )}
        </Typography>
      </LightTooltip>
    );
    switch (type) {
      case 'new account': {
        const fullText = `New account ${val?.account} has been created.`;
        const content = (
          <>
            New account <span style={{ fontWeight: 'bold' }}>{val?.account}</span> has been created.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }

      case 'new lead': {
        const fullText = `New Lead ${val?.lead} has been created.`;
        const content = (
          <>
            New Lead <span style={{ fontWeight: 'bold' }}>{val?.lead}</span> has been created.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }

      case 'lead stage changed': {
        const fullText = `Lead ${val?.lead} has been moved from ${val?.oldStage} to ${val?.newStage}.`;
        const content = (
          <>
            Lead <span style={{ fontWeight: 'bold' }}>{val?.lead}</span> has been moved from{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.oldStage}</span> to{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.newStage}</span>.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }

      case 'deal amount changed': {
        const fullText = `The amount for deal ${val?.deal} has been updated $${
          val?.oldAmount || 0
        } to $${val?.newAmount || 0}.`;
        const content = (
          <>
            The amount for deal <span style={{ fontWeight: 'bold' }}>{val?.deal}</span> has been
            updated
            <span style={{ fontWeight: 'bold' }}> ${val?.oldAmount || 0}</span> to{' '}
            <span style={{ fontWeight: 'bold' }}> ${val?.newAmount || 0}</span>.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }

      case 'deal stage changed': {
        const fullText = `Deal ${val?.deal} has been moved from ${val?.oldStage} to ${val?.newStage}.`;
        const content = (
          <>
            Deal <span style={{ fontWeight: 'bold' }}>{val?.deal}</span> has been moved from{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.oldStage}</span> to{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.newStage}</span>.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }

      case 'new deal': {
        const fullText = `New Deal ${val?.deal} has been created.`;
        const content = (
          <>
            New Deal <span style={{ fontWeight: 'bold' }}>{val?.deal}</span> has been created.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }

      case 'lead activity': {
        const fullText = `New activity "${val?.activitySubject}" has been added to the lead ${val?.lead}.`;
        const content = (
          <>
            New activity <span style={{ fontWeight: 'bold' }}>"{val?.activitySubject}"</span> has
            been added to the lead <span style={{ fontWeight: 'bold' }}>{val?.lead}</span>.
          </>
        );
        return createTextWithTooltip(content, fullText);
      }
      default:
        return val?.isSlack ? createTextWithTooltip(val?.text, val?.text) : null;
    }
  };
  const type = val?.type?.toLocaleLowerCase();
  const dateField =
    type === 'lead stage changed' ||
    type === 'new lead' ||
    type === 'lead activity' ||
    type === 'new account'
      ? val?.date // For new account, new lead, and lead activity, use the "created date"
      : val?.dealCloseDate; // For deal-related events, use the "close date"

  const createdBy =
    type === 'deal amount changed' || type === 'deal stage changed' || type === 'lead stage changed'
      ? getCreatedBy(val?.dealModifiedBy || val?.dealCreatedBy, 'Modified By')
      : type === 'new lead' ||
        type === 'lead activity' ||
        type === 'lead stage changed' ||
        type === 'deal amount changed' ||
        type === 'deal stage changed' ||
        type === 'new deal'
      ? getCreatedBy(
          val?.createdByLead || val?.dealCreatedBy || val?.createdByActivity || val?.createdByLead,
          'Created By',
        )
      : getCreatedBy(val?.createdBy, 'Created By');

  const dateDetails = getDateDetails(
    dateField,
    type === 'new lead' || type === 'lead activity' || type === 'new account'
      ? 'Created Date'
      : 'Close Date',
  );

  const stageDetails =
    type === 'lead stage changed' ||
    type === 'deal amount changed' ||
    type === 'deal stage changed' ||
    type === 'new deal'
      ? getStageDetails(val?.newStage || val?.dealStage, 'Stage')
      : null;

  const statusDetails = type === 'new lead' ? getStatusDetails(val?.leadStatus) : null;
  return (
    <CustomCard
      className={styles['card-outer']}
      styles={{
        // borderRadius: '14px',
        // padding: '12px',
        backgroundColor: slackConnectBtn
          ? '#4956F433'
          : index < newData || val?.new
          ? 'rgba(73, 86, 244, 0.1)'
          : zeplinColor.Neutral1,
        border: `1px solid ${zeplinColor.Neutral5}`,
        width: '100%',
        ...(val?.isSlack || slackConnectBtn
          ? {
              height: getCardHeight(),
              minHeight: getCardHeight(),
              // Remove the old overflow logic
            }
          : {}),
        // display: 'flex',
        // flexDirection: 'column',
        // justifyContent: 'space-between',
      }}
    >
      <Box
        className={styles['card-inner']}
        sx={{
          display: 'flex',
          // justifyContent: 'space-between',
          alignItems: 'flex-start',
          // gap: '12px',
        }}
      >
        {loading ? (
          <>
            <Skeleton variant='rounded' width={32} height={32} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  // Optionally adjust other styles here
                }}
              />
              <Box sx={{ display: 'flex', gap: '12px', flex: 1, alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    flex: 1,
                    justifyContent: 'flex-end',
                    textAlign: 'left',
                  }}
                >
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                      // Optionally adjust other styles here
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    flex: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                      // Optionally adjust other styles here
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    flex: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                      // Optionally adjust other styles here
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                borderRadius: '8px',
                border: `1px solid ${zeplinColor.Neutral5}`,
                // padding: '2px',
                backgroundColor: slackConnectBtn ? zeplinColor.Neutral1 : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
              }}
            >
              {val?.isSlack || slackConnectBtn ? (
                <SlackIcon width={19} height={19} />
              ) : (
                <img src='/assets/salesforce.png' width={30} height={30} />
              )}
            </Box>
            <Box
              className={slackConnectBtn ? '' : styles['content-gap']}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                ...((val?.isSlack || slackConnectBtn) && { gap: '8px' }),
                flex: 1,
              }}
            >
              <Box
                className={styles['subject-gap']}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  // flexWrap: 'nowrap',
                }}
              >
                {slackConnectBtn ? (
                  <Typography variant='responsiveHeading4'>Connect Slack to Get Started</Typography>
                ) : (
                  getSubject(type, val)
                )}

                {slackConnectBtn ? (
                  <img
                    src={'/assets/close-icon.svg'}
                    className={styles['icon-18']}
                    alt='close'
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowSlackConnect(false)}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '65px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-start',
                      minWidth: '65px',
                    }}
                  >
                    <Typography
                      variant='responsiveBody3'
                      color={index < newData ? zeplinColor.Brand1 : zeplinColor.Neutral8}
                      sx={{
                        whiteSpace: 'nowrap', // Prevent wrapping
                        overflow: 'hidden', // Hide overflow
                        textOverflow: 'ellipsis', // Show ellipsis if overflowing
                        fontSize: { xs: '11px', sm: '12px', xl: '14px' }, // Make the font smaller on mobile
                        textAlign: 'right', // Right align text
                      }}
                    >
                      {timeText}
                    </Typography>
                  </Box>
                )}
              </Box>
              {isFirstCard && !slackConnectBtn && (
                <Box
                  sx={{
                    padding: '4px 8px',
                    borderRadius: '50px',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    backgroundColor: zeplinColor.Neutral4,
                    width: 'fit-content',
                  }}
                >
                  <img
                    src={'/assets/slack-channel-lock.svg'}
                    style={{ width: '14px', height: '14px' }}
                  />
                  <Typography variant='label1' color={zeplinColor.BasicBlack}>
                    {val?.channel || '-'}
                  </Typography>
                </Box>
              )}
              {slackConnectBtn ? (
                <Typography variant='responsiveBody3'>
                  Connect your Slack for real-time messaging, notifications and collaboration within
                  Huddle
                </Typography>
              ) : val?.isSlack ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    // marginTop: '8px',
                  }}
                >
                  {/* <SlackUser className={styles['slack-user']} /> */}
                  <img src={val?.sender_img} className={styles['slack-user']} />
                  <Typography
                    variant='responsiveBody3'
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.4,
                      // height: '2.8em', // Enforce exact height for 2 lines (2 * 1.4em)
                      maxWidth: { xs: '170px', sm: '250px', md: '300px', lg: '400px' },
                    }}
                  >
                    {val?.sender}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between', // Changed from flex-start to space-between
                    alignItems: 'center',
                    width: '100%', // Added to ensure full width
                    // marginTop: '8px', // Add some spacing from the subject line
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      // flex: 1,
                      // width: '100%',
                      justifyContent: 'flex-end',
                      textAlign: 'left',
                    }}
                  >
                    {createdBy}
                  </Box>
                  {stageDetails ? (
                    // <Divider
                    //   component='div'
                    //   sx={{
                    //     borderColor: zeplinColor.Neutral5,
                    //     borderWidth: 1,
                    //     height: '20px',
                    //     margin: 'auto 0px',
                    //   }}
                    // />
                    <Box
                      sx={{
                        height: '20px',
                        width: '1px',
                        backgroundColor: zeplinColor.Neutral5,
                        display: 'inline-block',
                        marginX: '4px', // Add some margin on both sides
                      }}
                    />
                  ) : (
                    ''
                  )}
                  {stageDetails}
                  {statusDetails ? (
                    // <Divider
                    //   component='div'
                    //   sx={{
                    //     borderColor: zeplinColor.Neutral5,
                    //     borderWidth: 1,
                    //     height: '20px',
                    //     margin: 'auto 0px',
                    //   }}
                    // />
                    <Box
                      sx={{
                        height: '20px',
                        width: '1px',
                        backgroundColor: zeplinColor.Neutral5,
                        display: 'inline-block',
                        marginX: '4px', // Add some margin on both sides
                      }}
                    />
                  ) : (
                    ''
                  )}
                  {statusDetails}
                  {dateDetails ? (
                    // <Divider
                    //   component='div'
                    //   sx={{
                    //     borderColor: zeplinColor.Neutral5,
                    //     borderWidth: 1,
                    //     height: '20px',
                    //     margin: 'auto 0px',
                    //   }}
                    // />
                    <Box
                      sx={{
                        height: '20px',
                        width: '1px',
                        backgroundColor: zeplinColor.Neutral5,
                        display: 'inline-block',
                        marginX: '4px', // Add some margin on both sides
                      }}
                    />
                  ) : (
                    ''
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      // flex: 1,
                      justifyContent: 'flex-end',
                      whiteSpace: 'nowrap',
                      textWrap: 'nowrap',
                    }}
                  >
                    {/* <LightTooltip
                    title={
                      <Typography variant='body3' color={zeplinColor.BasicBlack}>
                        {val?.dealStage}
                      </Typography>
                    }
                    placement='bottom-end'
                    style={{ backgroundColor: zeplinColor.Neutral1, borderRadius: '8px' }}
                  >
                    <Typography variant='body3'>
                      {val?.dealStage?.slice(0, 15)}
                      {val?.dealStage?.length > 15 && '...'}
                    </Typography>
                  </LightTooltip>
                  <Typography variant='label1'>Stage</Typography> */}

                    {dateDetails}
                  </Box>
                </Box>
              )}
              {isFirstCard && total > 1 && !isExpanded && (
                <Typography
                  variant='responsiveBody3'
                  color={zeplinColor.Brand1}
                  onClick={() => handleCardClick()}
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.4,
                    // height: '2.8em', // Enforce exact height for 2 lines (2 * 1.4em)
                    // maxWidth: { xs: '170px', sm: '250px', md: '300px', lg: '400px' },
                  }}
                >
                  {isExpanded
                    ? 'Show less'
                    : `${total - 1} more notification${total - 1 > 1 ? 's' : ''}`}
                </Typography>
              )}
              {slackConnectBtn && (
                <Link
                  // to={`https://slack.com/oauth?client_id=1547360932450.9088642262659&user_scope=%27channels%3Aread%2Cchannels%3Ahistory%2Cgroups%3Aread%2Cgroups%3Awrite%2Cchat%3Awrite%2Cgroups%3Ahistory%2Cim%3Aread%2Cim%3Ahistory%2Cmpim%3Aread%2Cmpim%3Ahistory%27&redirect_uri=${process.env.REACT_APP_REDIRECT}`}
                  to={`https://slack.com/oauth/v2/authorize?${queryString.stringify({
                    client_id: '1547360932450.9088642262659',
                    // scope: 'channels:manage,channels:read,channels:history,groups:read,groups:write,groups:history,im:read,im:history,mpim:read,mpim:history',
                    user_scope:
                      'users:read,channels:read,channels:history,groups:read,groups:write,chat:write,groups:history,im:read,im:history,mpim:read,mpim:history',
                    redirect_uri: `${process.env.REACT_APP_REDIRECT}`,
                  })}`}
                  // target='_blank'
                  // rel='noopener noreferrer'
                  style={{ color: zeplinColor.Neutral1, textDecoration: 'none', width: '78px' }}
                  onClick={() => {
                    handleRedirect();
                  }}
                >
                  <CustomButton
                    disabled={false}
                    onClick={() => {}}
                    variant='contained'
                    color={'primary'}
                    buttonVariant='buttonSmall'
                    style={{
                      width: '100%',
                      borderRadius: '8px',
                      height: '32px',
                      padding: '6px 12px',
                      '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                    }}
                    textStyle={{ color: zeplinColor.Neutral1 }}
                    text={'Connect'}

                    // tabindex={4}
                  />
                </Link>
              )}
            </Box>
          </>
        )}
      </Box>
    </CustomCard>
  );
};

export default ActivityCard;
